import React from 'react';
import { motion } from 'framer-motion';
import { Check, Repeat, GitBranch, Shield, ShieldCheck, Plug, Workflow, Zap, Database } from 'lucide-react';
import { Box, Flex, Text } from '@chakra-ui/react';

const features = [
  {
    id: 'sync',
    icon: Repeat,
    title: 'Zero Manual Fixes',
    description: 'Stop wasting hours reconnecting work or repairing broken links. DatLynk keeps Jira and ClickUp aligned so updates carry over without duplicate cleanup.',
    subtext: 'Two-way sync that keeps work connected.',
    bullets: ['Hierarchy-aware updates', 'Bidirectional field sync', 'No duplicate cleanup'],
    color: 'brand.primary',
    hex: '#2563EB',
    visual: (
      <Flex direction="column" alignItems="center" justifyContent="center" gap={4} w="100%" h="100%" position="relative">
        <Flex alignItems="center" justifyContent="center" gap={6} position="relative">
          <Box p={4} bg="landing.card" borderRadius="2xl" boxShadow="xl" zIndex={2} border="1px solid" borderColor="brand.100">
            <img src="https://cdn.simpleicons.org/jira/0052CC" width="40" height="40" alt="Jira" />
          </Box>
          <Box position="relative" w={{ base: "60px", md: "100px" }} h="2px" bg="brand.200" zIndex={1}>
            <motion.div 
              style={{ position: 'absolute', top: '-3px', left: 0, width: '8px', height: '8px', background: '#2563EB', borderRadius: '50%', boxShadow: '0 0 10px #2563EB' }}
              animate={{ left: ['0%', '100%'] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
            />
            <motion.div 
              style={{ position: 'absolute', bottom: '-3px', right: 0, width: '8px', height: '8px', background: '#7B68EE', borderRadius: '50%', boxShadow: '0 0 10px #7B68EE' }}
              animate={{ right: ['0%', '100%'] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'linear', delay: 0.75 }}
            />
          </Box>
          <Box p={4} bg="landing.card" borderRadius="2xl" boxShadow="xl" zIndex={2} border="1px solid" borderColor="brand.100">
            <img src="https://cdn.simpleicons.org/clickup/7B68EE" width="40" height="40" alt="ClickUp" />
          </Box>
        </Flex>
        <Flex align="center" gap={1.5} px={3} py={1} bg="success.50" borderRadius="full" border="1px solid" borderColor="success.100">
          <Box w={1.5} h={1.5} borderRadius="full" bg="success.500" style={{ boxShadow: '0 0 4px rgba(52,211,153,0.8)' }} />
          <Text fontSize="xs" fontWeight="800" color="success.600" letterSpacing="0.06em">LIVE SYNC ACTIVE</Text>
        </Flex>
      </Flex>
    )
  },
  {
    id: 'control',
    icon: GitBranch,
    title: 'Keep Your Hierarchy',
    description: 'An epic in Jira should still look like the right parent work in ClickUp. DatLynk preserves project structure so teams keep the same context on both sides.',
    subtext: 'Keep epics, tasks, and subtasks perfectly in sync.',
    bullets: ['Preserved parent-child relationships', 'Mapped fields stay aligned', 'Structure-first mirroring'],
    color: 'brand.violet',
    hex: '#8B5CF6',
    visual: (
      <Box w="100%" maxW="300px" mx="auto" py={4} pl={2} position="relative">
        
        {/* Level 1: Epic */}
        <Flex alignItems="center" justify="space-between" bg="landing.card" p={3} borderRadius="lg" boxShadow="sm" borderLeft="4px solid" borderColor="brand.violet" position="relative" zIndex={2}>
          <Flex alignItems="center" gap={3}>
            <Box color="brand.violet" display="flex"><Database size={16} /></Box>
            <Text fontSize="sm" fontWeight="800" color="text.main">Epic: Q4 Launch</Text>
          </Flex>
          <Box px={2} py={0.5} bg="brand.subtle" color="brand.primary" borderRadius="md" fontSize="10px" fontWeight="800">SYNCED</Box>
        </Flex>
        
        {/* Children Container 1 */}
        <Box position="relative" ml={6} mt={3}>
          {/* Vertical connecting line from Level 1 */}
          <Box position="absolute" left="-12px" top="-12px" bottom="20px" w="2px" bg="brand.200" zIndex={1} />
          
          {/* Level 2: Task */}
          <Box position="relative" zIndex={2}>
             {/* Horizontal connector */}
             <Box position="absolute" left="-12px" top="50%" transform="translateY(-50%)" w="12px" h="2px" bg="brand.200" />
             <Flex alignItems="center" justify="space-between" bg="landing.card" p={3} borderRadius="lg" boxShadow="sm" borderLeft="4px solid" borderColor="brand.400">
                <Flex alignItems="center" gap={3}>
                  <Box color="brand.400" display="flex"><Check size={16} /></Box>
                  <Text fontSize="sm" fontWeight="700" color="text.main">Task: Core Infrastructure</Text>
                </Flex>
             </Flex>
          </Box>
          
          {/* Children Container 2 */}
          <Box position="relative" ml={6} mt={3}>
             {/* Vertical connecting line from Level 2 */}
             <Box position="absolute" left="-12px" top="-12px" bottom="20px" w="2px" bg="brand.200" zIndex={1} />
             
             {/* Level 3: Subtask */}
             <Box position="relative" zIndex={2}>
                {/* Horizontal connector */}
                <Box position="absolute" left="-12px" top="50%" transform="translateY(-50%)" w="12px" h="2px" bg="brand.200" />
                <Flex alignItems="center" justify="space-between" bg="landing.card" p={3} borderRadius="lg" boxShadow="sm" borderLeft="4px solid" borderColor="brand.200">
                  <Flex alignItems="center" gap={3}>
                    <Box color="brand.300" display="flex"><Zap size={14} /></Box>
                    <Text fontSize="xs" fontWeight="700" color="text.secondary">Subtask: OAuth Flow</Text>
                  </Flex>
                </Flex>
             </Box>
          </Box>
        </Box>
      </Box>
    )
  },
  {
    id: 'automation',
    icon: Workflow,
    title: 'Built to Expand with You',
    description: 'Start with Jira and ClickUp today on a sync foundation designed to support broader workflows as your stack grows.',
    subtext: 'A structure-aware platform, starting with the connectors you need now.',
    bullets: ['Jira and ClickUp available now', 'Connector foundation designed to scale', 'Broader workflows can plug into the same model'],
    color: 'brand.cyan',
    hex: '#22D3EE',
    visual: (
      <Box position="relative" w="240px" h="240px" mx="auto">
         {/* Hub Rings */}
         <Box position="absolute" inset={0} borderRadius="full" border="1px solid" borderColor="rgba(8, 145, 178, 0.2)" />
         <Box position="absolute" inset="40px" borderRadius="full" border="1px dashed" borderColor="rgba(8, 145, 178, 0.4)" style={{ animation: 'spin-slow 15s linear infinite' }} />
         
         {/* Center Hub */}
         <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" w="72px" h="72px" bg="brand.cyan" borderRadius="2xl" display="flex" alignItems="center" justifyContent="center" boxShadow="0 12px 32px rgba(8, 145, 178, 0.4)" zIndex={10}>
            <Repeat size={32} color="white" />
         </Box>

         {/* Nodes */}
         <Box position="absolute" top="-10px" left="50%" transform="translateX(-50%)" p={2} bg="landing.card" borderRadius="lg" boxShadow="md" zIndex={5}>
            <img src="https://cdn.simpleicons.org/jira/0052CC" width="24" height="24" alt="Jira" />
         </Box>
         <Box position="absolute" bottom="-10px" left="50%" transform="translateX(-50%)" p={2} bg="landing.card" borderRadius="lg" boxShadow="md" zIndex={5}>
            <img src="https://cdn.simpleicons.org/clickup/7B68EE" width="24" height="24" alt="ClickUp" />
         </Box>
         <Box position="absolute" top="50%" left="-10px" transform="translateY(-50%)" p={2} bg="landing.card" borderRadius="lg" boxShadow="md" zIndex={5}>
            <img src="https://cdn.simpleicons.org/notion/000000" width="24" height="24" alt="Notion" />
         </Box>
         <Box position="absolute" top="50%" right="-10px" transform="translateY(-50%)" p={2} bg="landing.card" borderRadius="lg" boxShadow="md" zIndex={5}>
          <img src="https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/slack.svg" width="24" height="24" alt="Slack" />
         </Box>
      </Box>
    )
  },
  {
    id: 'security',
    icon: Shield,
    title: 'Secure & Reliable Sync',
    description: 'DatLynk secures access with OAuth and protects sync traffic so teams can connect Jira and ClickUp workflows with confidence.',
    subtext: 'Secure credentials, controlled access, and reliable sync visibility.',
    bullets: ['Protected credentials and sync traffic', 'OAuth-based access control', 'Not a second system of record'],
    color: '#10B981',
    hex: '#10B981',
    visual: (
      <Flex direction="column" alignItems="center" justifyContent="center" gap={6} h="100%">
        <Box position="relative">
           <motion.div
              animate={{ y: [-4, 4, -4] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
           >
              <Box w="80px" h="80px" bg="#10B981" borderRadius="2xl" display="flex" alignItems="center" justifyContent="center" boxShadow="0 16px 40px rgba(16, 185, 129, 0.4)" zIndex={2} position="relative">
                 <Shield size={40} color="white" strokeWidth={2.5} />
              </Box>
           </motion.div>
           
           {/* Protection rings */}
           <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" w="120px" h="120px" borderRadius="full" border="2px solid" borderColor="rgba(16, 185, 129, 0.2)" zIndex={1} />
           <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" w="160px" h="160px" borderRadius="full" border="1px dashed" borderColor="rgba(16, 185, 129, 0.3)" zIndex={1} style={{ animation: 'spin-slow 20s linear infinite' }} />
        </Box>

        <Flex gap={2} bg="landing.card" px={4} py={2} borderRadius="xl" boxShadow="md" zIndex={2} border="1px solid" borderColor="green.100">
           <ShieldCheck size={16} color="#10B981" />
           <Text fontSize="xs" fontWeight="800" color="green.700" letterSpacing="0.05em">AES-256 ENCRYPTED</Text>
        </Flex>
      </Flex>
    )
  },
  {
    id: 'setup',
    icon: Plug,
    title: 'Setup in Under 60 Seconds',
    description: 'Connect Jira and ClickUp, review your mappings, and turn on sync in minutes.',
    subtext: 'Authorize once, map once, and let DatLynk keep both sides aligned.',
    bullets: ['Secure OAuth connection', 'Hierarchy-aware mapping review', 'Fast activation and monitoring'],
    color: '#F59E0B',
    hex: '#F59E0B',
    visual: (
      <Flex direction="column" gap={4} w="100%" maxW="280px" mx="auto">
        {[
          { text: "Secure Connection", status: "success", delay: 0 },
          { text: "Analyze Hierarchy", status: "success", delay: 0.5 },
          { text: "Configure Rules", status: "success", delay: 1.0 },
          { text: "Active Sync", status: "active", delay: 1.5 }
        ].map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: item.delay, duration: 0.5 }}
          >
            <Flex alignItems="center" justify="space-between" bg="landing.card" p={3} borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="landing.border">
               <Flex alignItems="center" gap={3}>
                 <Box w={6} h={6} borderRadius="full" bg={item.status === 'success' ? 'green.100' : 'brand.100'} display="flex" alignItems="center" justifyContent="center">
                    {item.status === 'success' 
                      ? <Box color="#10B981" display="flex"><Check size={12} strokeWidth={3} /></Box>
                      : <Box color="brand.primary" display="flex"><Zap size={12} /></Box>
                    }
                 </Box>
                 <Text fontSize="xs" fontWeight="700" color="text.main">{item.text}</Text>
               </Flex>
               {item.status === 'active' && (
                 <motion.div animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 2, repeat: Infinity }}>
                   <Box w={2} h={2} bg="brand.primary" borderRadius="full" />
                 </motion.div>
               )}
            </Flex>
          </motion.div>
        ))}
      </Flex>
    )
  },
];

const Features = () => {
  return (
    <Box
      as="section"
      id="features"
      bg="landing.bg"
      py={{ base: 24, md: 32 }}
      fontFamily="body"
      position="relative"
      overflow="hidden"
    >
      {/* Background Decor */}
      <Box 
        position="absolute" 
        top="15%" right="0" 
        w="30%" h="30%" 
        bg="radial-gradient(circle, rgba(123, 104, 238, 0.05) 0%, transparent 70%)" 
        pointerEvents="none" 
      />
      <Box 
        position="absolute" 
        bottom="10%" left="-5%" 
        w="40%" h="40%" 
        bg="radial-gradient(circle, rgba(37, 99, 235, 0.03) 0%, transparent 70%)" 
        pointerEvents="none" 
      />
      
      <Box maxW="1100px" mx="auto" px={{ base: 5, md: 8 }} position="relative" zIndex={1}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.8 }}
        >
          <Box textAlign="center" mb={20}>
            <Flex
              display="inline-flex"
              alignItems="center"
              px={3} py={1.5}
              borderRadius="full"
              bg="brand.subtle"
              borderWidth="1px"
              borderColor="landing.border"
              mb={4}
              mx="auto"
            >
              <Text fontSize="xs" fontWeight="800" color="brand.primary" letterSpacing="0.05em">WHAT DATLYNK HANDLES</Text>
            </Flex>
            <Text
              as="h2"
              fontSize={{ base: '3xl', md: '5xl' }}
              fontWeight="900"
              color="text.main"
              letterSpacing="-0.03em"
              mb={4}
            >
              Core <Box as="span" color="brand.primary">Capabilities</Box>
            </Text>
            <Text fontSize="lg" color="text.muted" maxW="600px" mx="auto" lineHeight="1.7">
              DatLynk is live today for Jira and ClickUp and solves the hardest part first: preserving{' '}
              <Box as="span" color="text.main" fontWeight="700">structure, mappings, and updates</Box> without manual cleanup.
            </Text>
          </Box>
        </motion.div>

        {/* Feature blocks */}
        <Flex direction="column" gap={{ base: 16, md: 24 }}>
          {features.map((feature, index) => {
            const isEven = index % 2 === 0;
            return (
              <React.Fragment key={feature.id}>
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                >
                  <Flex
                    alignItems="center"
                    direction={{ base: 'column', md: isEven ? 'row' : 'row-reverse' }}
                    gap={{ base: 10, md: 16 }}
                  >
                    {/* Text Area */}
                    <Box flex={1}>
                      <Flex alignItems="center" gap={4} mb={6}>
                        <Flex
                          w="48px" h="48px"
                          borderRadius="xl"
                          bg={`${feature.hex}15`}
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                          border="1px solid"
                          borderColor={`${feature.hex}30`}
                        >
                          <Box color={feature.color} display="flex">
                            <feature.icon size={24} strokeWidth={2} />
                          </Box>
                        </Flex>
                        <Text
                          as="h3"
                          fontSize="2xl"
                          fontWeight="800"
                          color="text.main"
                          letterSpacing="-0.01em"
                        >
                          {feature.title}
                        </Text>
                      </Flex>

                      <Text fontSize="md" color="text.secondary" mb={4} lineHeight="1.8">
                        {feature.description}
                      </Text>
                      <Text fontSize="sm" color="text.muted" mb={6} lineHeight="1.75" fontWeight="500">
                        {feature.subtext}
                      </Text>

                      <Flex direction="column" gap={3} mb={8}>
                        {feature.bullets.map((bullet, i) => (
                          <Flex key={i} alignItems="center" gap={3}>
                            <Flex
                              w="20px" h="20px"
                              borderRadius="full"
                              bg={`${feature.hex}15`}
                              alignItems="center"
                              justifyContent="center"
                              flexShrink={0}
                            >
                              <Box color={feature.color} display="flex"><Check size={12} strokeWidth={3} /></Box>
                            </Flex>
                            <Text fontSize="sm" fontWeight="600" color="text.main">{bullet}</Text>
                          </Flex>
                        ))}
                      </Flex>
                    </Box>

                    {/* Highly Visual Infographic Card */}
                    <Box
                      flex={1}
                      w="100%"
                      p={8}
                      borderRadius="3xl"
                      bg="transparent"
                      borderWidth="1px"
                      borderColor="landing.border"
                      minH={{ base: 'auto', md: '360px' }}
                      py={{ base: 10, md: 8 }}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      position="relative"
                      overflow="hidden"
                      boxShadow="sm"
                    >
                      {/* Background accent color */}
                      <Box 
                        position="absolute" 
                        top="-20%" right="-20%" 
                        w="70%" h="70%" 
                        bg={`radial-gradient(circle, ${feature.hex}15 0%, transparent 70%)`} 
                        zIndex={0} 
                      />
                      
                      <Box zIndex={1} w="100%" h="100%">
                         {feature.visual}
                      </Box>
                    </Box>
                  </Flex>
                </motion.div>
              </React.Fragment>
            );
          })}
        </Flex>
      </Box>
      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default Features;
